import { BreakdownDonut, OTHER_COLOR, type BreakdownBucket } from "./BreakdownDonut";
import { useLanguage } from "@/shared/hooks/use-language";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { formatPercent, parsePercentToAmountScale } from "@/shared/lib/amount";
import {
  currencyAllocationOf,
  hasUnknownCurrencyValue,
  type CurrencyAllocation,
} from "@/features/portfolio/lib/currency-allocation";
import { isNegativeNavOf, type Position, type PositionSummary } from "@/features/portfolio/api/position";

interface CurrencyAllocationDonutProps {
  positions: Position[];
  summary?: PositionSummary | null;
  summaryUnavailable?: boolean;
}

// The currency set is open, so a color goes by rank rather than by currency.
const COLORS_BY_RANK = [1, 2, 3, 4, 5, 6].map((chart) => `hsl(var(--chart-${chart}))`);

const UNKNOWN_CURRENCY_KEY = "unknown-currency";

const NOTICE_KEY_BY_STATE: Record<Exclude<CurrencyAllocation["state"], "weights" | "no-buckets">, string> = {
  "zero-nav": "portfolio.currencyAllocationZeroNav",
  unreconciled: "portfolio.currencyAllocationUnreconciled",
  "rate-missing": "portfolio.currencyAllocationFxUnavailable",
  pending: "portfolio.currencyAllocationPending",
};

export const CurrencyAllocationDonut = ({ positions, summary, summaryUnavailable }: CurrencyAllocationDonutProps) => {
  const { t } = useLanguage();
  const { numberFormat } = useNumberFormat();

  // Same rule as the asset-class card: the page states a failed summary in its own card.
  if (summaryUnavailable) return null;

  const allocation = currencyAllocationOf(summary);
  const hasUnknown = hasUnknownCurrencyValue(positions);

  const buckets: BreakdownBucket[] = allocation.state === "weights"
    ? allocation.weights.map((currencyWeight, rank) => ({
      key: currencyWeight.currency,
      label: currencyWeight.currency,
      weightLabel: formatPercent(currencyWeight.weight, numberFormat),
      share: parsePercentToAmountScale(currencyWeight.weight),
      color: COLORS_BY_RANK[rank] ?? OTHER_COLOR,
    }))
    : [];
  if (hasUnknown) {
    buckets.push({
      key: UNKNOWN_CURRENCY_KEY,
      label: t("portfolio.currencyAllocationUnknown"),
      weightLabel: t("portfolio.currencyAllocationUnknownWeight"),
      share: null,
      color: OTHER_COLOR,
      testId: "currency-allocation-unknown",
    });
  }

  return (
    <BreakdownDonut
      title={t("portfolio.currencyAllocationTitle")}
      testIdPrefix="currency-allocation"
      buckets={buckets}
      notice={noticeOf(allocation, hasUnknown, positions, t)}
      wholeIsNegative={isNegativeNavOf(summary)}
    />
  );
};

function noticeOf(
  allocation: CurrencyAllocation,
  hasUnknown: boolean,
  positions: Position[],
  t: (key: string) => string,
): string | null {
  if (allocation.state === "weights") return null;
  if (allocation.state !== "no-buckets") return t(NOTICE_KEY_BY_STATE[allocation.state]);
  // No currency was valued at all. When valued positions with no currency are the reason,
  // saying prices are on their way would name an outage that is not happening.
  if (hasUnknown) return t("portfolio.currencyAllocationUnknownOnly");
  if (positions.length === 0) return null;
  return t("portfolio.currencyAllocationPending");
}
